import { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FormControl, Grid, InputAdornment, OutlinedInput } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import FieldNames from '../../defaults/FieldNames.json';
import {
    removeLeadingZeros,
    removeLetters,
    removeNegative,
    removeDecimal,
    currencyFormat,
} from '../../utils/FormatHelper';
import { sumCostsMultiFunding } from '../../utils/CostsHelper';
import DetailsPopover from '../details/customPanels/DetailsPopover';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RecordCostAssignmentGridHandle {
    getValues: () => Record<string, any>;
    isValid: () => boolean;
}

export interface RecordCostAssignmentGridProps {
    selectedRow?: any;
    requiredFields?: string[];
    isTreatment?: boolean;
    isActual?: boolean;
    isPoint?: boolean;
    onChange?: (values: Record<string, any>) => void;
    onValidityChange?: (valid: boolean) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BLUE_ALIEN = '#1976d2';

const theme = createTheme({
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_ALIEN,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_ALIEN,
                        borderWidth: '2px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_ALIEN,
                        borderWidth: '2px',
                    },
                },
            },
        },
    },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const FF = FieldNames.fundingFields;
const FEF = FieldNames.featureFields;

/** Sanitise raw input: strip leading zeros, decimals, negatives, letters, then add commas. */
const sanitiseCurrencyInput = (raw: string): string => {
    const noLeadingZeros = removeLeadingZeros(raw);
    const noDecimals = removeDecimal(noLeadingZeros);
    const noNegatives = removeNegative(noDecimals);
    const noLetters = removeLetters(noNegatives);
    return currencyFormat(noLetters);
};

/** Resolve an initial cost value from a row, returning a formatted string or "0". */
const resolveInitialCost = (row: any, field: string): string => {
    if (!row) return '0';
    const val = row[field];
    if (val === '' || val === null || val === undefined) return '0';
    return currencyFormat(val.toString());
};

/** Parse a formatted cost string into a number. */
const parseCost = (value: string | number): number => {
    if (typeof value === 'number') return value;
    return parseInt(value.replace(/,/g, ''), 10) || 0;
};

// ─── Cost-field configuration ────────────────────────────────────────────────

interface CostFieldConfig {
    label: string;
    fundingField: string;
    featureField: string;
}

const COST_FIELDS: CostFieldConfig[] = [
    {
        label: 'Personnel Cost',
        fundingField: FF.personnelCost,
        featureField: FEF.estimatedPersonnelCost,
    },
    {
        label: 'Grants Fixed Cost',
        fundingField: FF.grantsFixedCost,
        featureField: FEF.estimatedGrantsFixedCost,
    },
    { label: 'Asset Cost', fundingField: FF.assetCost, featureField: FEF.estimatedAssetCost },
    {
        label: 'Contractual Cost',
        fundingField: FF.contractualCost,
        featureField: FEF.estimatedContractualCost,
    },
    { label: 'Other Cost', fundingField: FF.otherCost, featureField: FEF.estimatedOtherCost },
];

// ─── Sub-component: a single cost input row ──────────────────────────────────

interface CostInputRowProps {
    label: string;
    value: string;
    featureField: string;
    requiredFields: string[];
    isTreatment: boolean;
    isActual: boolean;
    isPoint: boolean;
    disabled?: boolean;
    onChange?: (sanitisedValue: string) => void;
}

function CostInputRow({
    label,
    value,
    featureField,
    requiredFields,
    isTreatment,
    isActual,
    isPoint,
    disabled = false,
    onChange,
}: CostInputRowProps) {
    const isRequired = requiredFields.includes(featureField);

    return (
        <FormControl variant='standard' sx={{ margin: '15px', minWidth: 120, width: '90%' }}>
            <ThemeProvider theme={theme}>
                <Grid container direction='row' size={12} style={{ alignItems: 'center' }}>
                    <Grid size={6}>
                        <span>
                            <label>{label}</label>
                            {isRequired && <span className='required'>*</span>}
                            <DetailsPopover
                                mode='edit'
                                field={featureField}
                                isTreatment={isTreatment}
                                categoryValue={null}
                                isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no}
                                isActuals={isActual}
                                agencyValue={null}
                                isBulkApproval={false}
                            />
                        </span>
                    </Grid>
                    <Grid size={6}>
                        <OutlinedInput
                            value={value}
                            fullWidth
                            disabled={disabled}
                            onChange={(e) => onChange?.(sanitiseCurrencyInput(e.target.value))}
                            startAdornment={<InputAdornment position='start'>$</InputAdornment>}
                        />
                    </Grid>
                </Grid>
            </ThemeProvider>
        </FormControl>
    );
}

// ─── Main component ──────────────────────────────────────────────────────────
//
// Imperative handle (via ref):
//   getValues()  – returns a snapshot of all current cost field values
//   isValid()    – returns true when the computed total cost is > 0
//

const RecordCostAssignmentGrid = forwardRef<
    RecordCostAssignmentGridHandle,
    RecordCostAssignmentGridProps
>(function RecordCostAssignmentGrid(
    {
        selectedRow,
        requiredFields = [],
        isTreatment = false,
        isActual = false,
        isPoint = false,
        onChange,
        onValidityChange,
    },
    ref
) {
    // ── Local state for each cost line ──────────────────────────────────
    const [personnelCost, setPersonnelCost] = useState('0');
    const [grantsFixedCost, setGrantsFixedCost] = useState('0');
    const [assetCost, setAssetCost] = useState('0');
    const [contractualCost, setContractualCost] = useState('0');
    const [otherCost, setOtherCost] = useState('0');
    const [totalCost, setTotalCost] = useState('0');

    // ── Initialise from selectedRow ─────────────────────────────────────
    useEffect(() => {
        setPersonnelCost(resolveInitialCost(selectedRow, FF.personnelCost));
        setGrantsFixedCost(resolveInitialCost(selectedRow, FF.grantsFixedCost));
        setAssetCost(resolveInitialCost(selectedRow, FF.assetCost));
        setContractualCost(resolveInitialCost(selectedRow, FF.contractualCost));
        setOtherCost(resolveInitialCost(selectedRow, FF.otherCost));
        setTotalCost(resolveInitialCost(selectedRow, FF.totalCost));
    }, [selectedRow]);

    // ── Recompute total when any sub-cost changes ───────────────────────
    const recomputeTotal = useCallback(
        (asset: string, grants: string, contractual: string, other: string, personnel: string) => {
            const newTotal = sumCostsMultiFunding(asset, grants, contractual, other, personnel);
            setTotalCost(currencyFormat(newTotal));
        },
        []
    );

    // ── Build a snapshot of all current values ──────────────────────────
    const buildValues = useCallback(
        () => ({
            [FF.personnelCost]: parseCost(personnelCost),
            [FF.grantsFixedCost]: parseCost(grantsFixedCost),
            [FF.assetCost]: parseCost(assetCost),
            [FF.contractualCost]: parseCost(contractualCost),
            [FF.otherCost]: parseCost(otherCost),
            [FF.totalCost]: parseCost(totalCost),
        }),
        [personnelCost, grantsFixedCost, assetCost, contractualCost, otherCost, totalCost]
    );

    // ── Validation: total must be > 0 ───────────────────────────────────
    const checkValidity = useCallback(() => {
        return parseCost(totalCost) > 0;
    }, [totalCost]);

    // ── Notify parent whenever values change ────────────────────────────
    useEffect(() => {
        onChange?.(buildValues());
    }, [buildValues, onChange]);

    // ── Notify parent when validity changes ─────────────────────────────
    useEffect(() => {
        onValidityChange?.(checkValidity());
    }, [checkValidity, onValidityChange]);

    // ── Imperative handle ───────────────────────────────────────────────
    useImperativeHandle(
        ref,
        () => ({
            getValues: buildValues,
            isValid: checkValidity,
        }),
        [buildValues, checkValidity]
    );

    // ── Map of fundingField → { setter, otherCostsForSum } ──────────────
    // The argument order expected by sumCostsMultiFunding is:
    //   (assetCost, grantsCost, contractualCost, otherCost, personnelCost)
    const handleChange = useCallback(
        (fundingField: string, sanitised: string) => {
            switch (fundingField) {
                case FF.personnelCost:
                    setPersonnelCost(sanitised);
                    recomputeTotal(
                        assetCost,
                        grantsFixedCost,
                        contractualCost,
                        otherCost,
                        sanitised
                    );
                    break;
                case FF.grantsFixedCost:
                    setGrantsFixedCost(sanitised);
                    recomputeTotal(assetCost, sanitised, contractualCost, otherCost, personnelCost);
                    break;
                case FF.assetCost:
                    setAssetCost(sanitised);
                    recomputeTotal(
                        sanitised,
                        grantsFixedCost,
                        contractualCost,
                        otherCost,
                        personnelCost
                    );
                    break;
                case FF.contractualCost:
                    setContractualCost(sanitised);
                    recomputeTotal(assetCost, grantsFixedCost, sanitised, otherCost, personnelCost);
                    break;
                case FF.otherCost:
                    setOtherCost(sanitised);
                    recomputeTotal(
                        assetCost,
                        grantsFixedCost,
                        contractualCost,
                        sanitised,
                        personnelCost
                    );
                    break;
                default:
                    break;
            }
        },
        [assetCost, grantsFixedCost, contractualCost, otherCost, personnelCost, recomputeTotal]
    );

    // ── Lookup from fundingField → current display value ────────────────
    const valueMap: Record<string, string> = {
        [FF.personnelCost]: personnelCost,
        [FF.grantsFixedCost]: grantsFixedCost,
        [FF.assetCost]: assetCost,
        [FF.contractualCost]: contractualCost,
        [FF.otherCost]: otherCost,
    };

    // ── Render ──────────────────────────────────────────────────────────
    return (
        <Fragment>
            {COST_FIELDS.map((cfg) => (
                <CostInputRow
                    key={cfg.fundingField}
                    label={cfg.label}
                    value={valueMap[cfg.fundingField]}
                    featureField={cfg.featureField}
                    requiredFields={requiredFields}
                    isTreatment={isTreatment}
                    isActual={isActual}
                    isPoint={isPoint}
                    onChange={(sanitised) => handleChange(cfg.fundingField, sanitised)}
                />
            ))}

            {/* Total Cost — read-only */}
            <CostInputRow
                label='Total Cost'
                value={totalCost}
                featureField={FEF.estimatedTotalCost}
                requiredFields={requiredFields}
                isTreatment={isTreatment}
                isActual={isActual}
                isPoint={isPoint}
                disabled
            />
        </Fragment>
    );
});

export default RecordCostAssignmentGrid;
